import { ReactNode } from 'react'

import { useTranslation } from 'react-i18next'

import {
  ActionMenu,
  CaseManagementIcon,
  ColorScheme,
  CreateIcon,
  CredentialsIcon,
  Box as DarkBox,
  Button as DarkButton,
  FacilityIcon,
  FloorsIcon,
  GatewayIcon,
  IncidentIcon,
  RuleIcon,
  SOPIcon,
  SecurityDeviceIcon,
} from '@hivewatch/dark'

import { CreateModalOptions, CreateModalType } from '../types/types'

interface DarkCreateButtonDropdownIProps {
  openModal: (modalType: CreateModalType) => void
  createModalOptions: CreateModalOptions
  colorScheme: ColorScheme
  disabledTooltipLabel?: string
}

// The sidebar icons ship at their own fixed viewBox, so scale them from the
// wrapper rather than forking a copy of each. 24px rather than the Design
// Library's 20×20 Action Menu Item glyph: this menu is a primary create
// affordance, not a row-level kebab, and the legacy menu it replaces drew
// these at 24px inside a 38px circle. See ENG-32861.
const MenuIcon = ({
  children,
  colorScheme,
}: {
  children: ReactNode
  colorScheme: ColorScheme
}) => (
  <DarkBox
    alignItems='center'
    boxSize='26px'
    // `icon/tertiary`, not `icon/secondary`: the Design Library reserves the
    // latter for muted glyphs, while tertiary is the one specified for plain
    // glyphs on a neutral surface that track the page foreground. Declaring it
    // here means every glyph below just inherits, so the ten stay in step.
    color={colorScheme === 'light' ? 'light.icon.tertiary' : 'icon.tertiary'}
    css={{ '& svg': { height: '100%', width: '100%' } }}
    display='inline-flex'
    justifyContent='center'
  >
    {children}
  </DarkBox>
)

/**
 * Rebrand glyphs for the Create menu, replacing the react-icons set the legacy
 * menu draws in blue circles. Keyed by `CreateModalType` so a new entry fails
 * to compile until it picks one, the same guarantee
 * `CREATE_MODAL_ANALYTICS_LABELS` gives for event names.
 *
 * Every glyph must resolve to `currentColor` so the single declaration on
 * `MenuIcon` drives all ten. Most default to it already; the three that
 * otherwise bake in their own `icon.secondary` are overridden here, so the
 * token lives in exactly one place.
 */
const MENU_ICONS: Record<CreateModalType, ReactNode> = {
  incident: <IncidentIcon />,
  facility: <FacilityIcon />,
  floor: <FloorsIcon size={20} />,
  device: <SecurityDeviceIcon size={20} />,
  sop: <SOPIcon color='currentColor' size={20} />,
  gateway: <GatewayIcon />,
  pacs: <CredentialsIcon color='currentColor' size={20} />,
  rule: <RuleIcon />,
  // The Rule icon, not WorkflowIcon: the legacy menu draws the Rule glyph for
  // both the Rule (Legacy) and Automation entries (ENG-33118).
  automation: <RuleIcon />,
  case: <CaseManagementIcon />,
}

/**
 * Rebrand re-skin of {@link CreateButtonDropdown}: the Design Library Action
 * Menu (`bg/card-elevated`, sharp corners, leading glyph) in place of the
 * legacy white panel with blue icon circles. Rendered only from
 * `CreateDropdown`'s dark branch, so it never reads a feature flag itself.
 *
 * Sized up from the Action Menu defaults — this is a primary create
 * affordance, and at 214px/12px/10px it read as a row-level kebab menu next
 * to the 430px panel it replaces. Every value below is a calibration point.
 */
export const DarkCreateButtonDropdown = ({
  openModal,
  createModalOptions,
  colorScheme,
  disabledTooltipLabel,
}: DarkCreateButtonDropdownIProps) => {
  const { t } = useTranslation()

  const items = createModalOptions
    .filter(({ hidden }) => !hidden)
    .map(({ key, label, disabled }) => ({
      'data-testid': `createButtonDropdown_menuItem_${key}`,
      disabled,
      icon: <MenuIcon colorScheme={colorScheme}>{MENU_ICONS[key]}</MenuIcon>,
      key,
      label,
      onClick: () => openModal(key),
      ...(disabled && disabledTooltipLabel
        ? { tooltipLabel: disabledTooltipLabel }
        : {}),
    }))

  return (
    <ActionMenu
      anchorRight
      colorScheme={colorScheme}
      fontSize='15px'
      itemLineHeight='20px'
      itemPadding='16px'
      items={items}
      maxHeight='80vh'
      minWidth='300px'
      tooltipPlacement='left'
      trigger={({ onClick }) => (
        <DarkButton
          colorScheme={colorScheme}
          data-testid='createButtonDropdown_button'
          onClick={onClick}
          rightIcon={<CreateIcon size={18} />}
          size='lg'
          variant='primary'
        >
          {t('common:create')}
        </DarkButton>
      )}
    />
  )
}
